/**
 * Accordions: one open at a time, with a smooth open/close
 * ---------------------------------------------------------------------------
 * Covers the <details> accordions (FAQ, footer columns and SEO link groups on
 * phones) and the product-details accordion on phones. Opening one closes any
 * other open one in the same group; both slide rather than jump.
 *
 * Only reacts to clicks, so the footer and SEO groups on desktop (always open,
 * not clickable) and the FAQ search (which opens matches itself) are left as
 * they are.
 */

const DURATION = 320;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Each <details> accordion and the element that holds its group. */
const DETAILS_GROUPS = [
  { item: '.wb-faq__item', group: '.wb-faq__list' },
  { item: '.wb-ft__col', group: '.wb-ft__main' },
  { item: '.wb-seo__group', group: '.wb-seo__groups' },
  { item: '.wb-gift', group: '.cart-drawer__summary' },
];

/* Slide cart: "This order is a gift" and "Discount" share one group. Discount
   is the theme's own accordion, so it is closed from here rather than
   intercepted. */
const CART_PAIR = '.wb-gift, .cart-discount details';

/* When a long item above closes, the one just opened moves up the page and
   can end up under the sticky header — bring its heading back into view. */
function keepInView(heading) {
  setTimeout(() => {
    const top = heading.getBoundingClientRect().top;
    if (top < 90) window.scrollBy({ top: top - 90, behavior: REDUCED.matches ? 'auto' : 'smooth' });
  }, DURATION + 20);
}

/* ---- <details> accordions ------------------------------------------------ */

function animateDetails(details, open) {
  const summary = details.querySelector('summary');
  if (!summary) return;

  const running = details._wbAnim;
  details._wbAnim = null;
  running?.cancel();

  if (REDUCED.matches) {
    details.open = open;
    if (!open) details._wbClosing = false;
    return;
  }

  const startHeight = details.offsetHeight;
  if (open) details.open = true;
  // Closed height = the summary plus the item's own padding and borders.
  const cs = getComputedStyle(details);
  const px = (...props) => props.reduce((sum, prop) => sum + (parseFloat(cs[prop]) || 0), 0);
  const borders = px('borderTopWidth', 'borderBottomWidth');
  const endHeight = open
    ? details.scrollHeight + borders
    : summary.offsetHeight + borders + px('paddingTop', 'paddingBottom');

  details.style.overflow = 'hidden';
  const anim = details.animate(
    { height: [`${startHeight}px`, `${endHeight}px`] },
    { duration: DURATION, easing: EASING }
  );
  details._wbAnim = anim;

  const finish = () => {
    if (details._wbAnim !== anim) return;
    details._wbAnim = null;
    details.style.overflow = '';
    if (!open) {
      details.open = false;
      details._wbClosing = false;
    }
  };
  anim.onfinish = finish;
  anim.oncancel = finish;
}

function onSummaryClick(event) {
  const summary = event.target.closest('summary');
  if (!summary) return;
  const details = summary.parentElement;
  if (!(details instanceof HTMLDetailsElement)) return;

  const rule = DETAILS_GROUPS.find((r) => details.matches(r.item));
  if (!rule) return;

  event.preventDefault();
  const willOpen = !details.open || details._wbClosing;

  if (willOpen) {
    const group = details.closest(rule.group) || details.parentElement;
    group.querySelectorAll(`${rule.item}[open]`).forEach((other) => {
      if (other === details) return;
      other._wbClosing = true;
      animateDetails(other, false);
    });
  }

  details._wbClosing = !willOpen;
  animateDetails(details, willOpen);
  if (willOpen) keepInView(summary);
}

document.addEventListener('click', onSummaryClick);

// `toggle` doesn't bubble, so listen in the capture phase.
document.addEventListener(
  'toggle',
  (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open || opened._wbClosing) return;
    if (!opened.matches(CART_PAIR)) return;
    const summary = opened.closest('.cart-drawer__summary');
    if (!summary) return;

    summary.querySelectorAll(CART_PAIR).forEach((other) => {
      if (other === opened || !other.open || other._wbClosing) return;
      other._wbClosing = true;
      animateDetails(other, false);
    });
  },
  true
);

/* ---- Product details accordion (phones) ---------------------------------- */

function animateBody(body, open) {
  const running = body._wbAnim;
  body._wbAnim = null;
  running?.cancel();

  if (REDUCED.matches) {
    body.hidden = !open;
    return;
  }

  const startHeight = body.hidden ? 0 : body.offsetHeight;
  body.hidden = false;
  const endHeight = open ? body.scrollHeight : 0;

  body.style.overflow = 'hidden';
  const anim = body.animate(
    { height: [`${startHeight}px`, `${endHeight}px`] },
    { duration: DURATION, easing: EASING }
  );
  body._wbAnim = anim;

  const finish = () => {
    if (body._wbAnim !== anim) return;
    body._wbAnim = null;
    body.style.overflow = '';
    body.hidden = !open;
  };
  anim.onfinish = finish;
  anim.oncancel = finish;
}

// Capture phase, so this runs before the tabs script's own toggle and can stop
// it — the tabs script would otherwise flip `hidden` with no animation.
document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest('[data-acc]');
    if (!button) return;
    const root = button.closest('[data-tabs]');
    const body = document.getElementById(button.getAttribute('aria-controls'));
    if (!root || !body) return;

    event.stopImmediatePropagation();
    const willOpen = button.getAttribute('aria-expanded') !== 'true';

    if (willOpen) {
      root.querySelectorAll('[data-acc][aria-expanded="true"]').forEach((other) => {
        if (other === button) return;
        other.setAttribute('aria-expanded', 'false');
        const otherBody = document.getElementById(other.getAttribute('aria-controls'));
        if (otherBody) animateBody(otherBody, false);
      });
    }

    button.setAttribute('aria-expanded', String(willOpen));
    animateBody(body, willOpen);
    if (willOpen) keepInView(button);
  },
  true
);
